/**
 * Whether a failed contact registration is worth trying again.
 *
 * Registering is what creates the user's `sdk_contacts` row, and that row is
 * the whole of their existence in chat: it mints the `contactPresenceId` every
 * subscription is gated on, and it is the ONLY thing contact search reads. A
 * user without one cannot be found by anybody, at any scope — not their
 * sub-org, not their org, not global — no matter that they can log in, join
 * meetings and appear in presence. They are simply not in the directory.
 *
 * The lookup beside it already retries five times with backoff, and its comment
 * says why: "a single network blip here used to mean silently no chat and
 * permanently offline". Registration had none of that — one attempt, a bare
 * `catch {}`, and a terminal error state. So one dropped POST cost a user their
 * directory presence for as long as they kept the tab open, with nothing
 * logged and nothing to point at.
 *
 * Retrying everything would be just as wrong in the other direction: a request
 * the server has rejected on its merits will be rejected identically five more
 * times, turning an instant, explicable failure into a slow, silent one. So the
 * decision is split — transport and server-side faults are worth another go,
 * a considered "no" is not.
 */

/** Attempts in total, including the first. Mirrors the lookup's budget. */
export const REGISTER_MAX_ATTEMPTS = 5

/** First backoff step; doubles each attempt — 1s, 2s, 4s, 8s. */
export const REGISTER_RETRY_BASE_MS = 1000

/**
 * The HTTP status behind a failure, or null when it never reached the server.
 *
 * The SDK throws `Error("contacts.registerSelf failed: 503")` — the status is
 * only ever available as text on the message, so it is parsed back out here
 * rather than each caller re-deriving it. A `fetch` that never completed
 * (offline, DNS, TLS, aborted) throws a TypeError carrying no status at all,
 * which is the null case and the most retryable of the lot.
 */
export function statusOf(err: unknown): number | null {
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : ''
  // Anchored at the end so a status inside a URL or an id cannot be mistaken
  // for the response code.
  const match = /\b(\d{3})\s*$/.exec(message.trim())
  if (!match) return null

  const status = Number(match[1])
  return status >= 100 && status <= 599 ? status : null
}

/**
 * Is another attempt worth making?
 *
 * Yes for anything that looks like weather — no response at all, a timeout, a
 * rate limit, or any 5xx. No for a 4xx the server meant, which will be refused
 * identically however many times it is asked.
 *
 * 401 is deliberately NOT retried. A token the SDK server will not accept is
 * not going to start being accepted four seconds later, and the fix lies
 * somewhere else entirely — which is exactly the kind of thing five silent
 * retries would bury.
 */
export function retryableFailure(err: unknown): boolean {
  const status = statusOf(err)
  if (status === null) return true // never reached the server
  if (status >= 500) return true // the server broke, not the request
  // Timeouts and rate limits are 4xx by number and transient by nature.
  return status === 408 || status === 425 || status === 429
}

/** How long to wait before attempt `n + 1` (zero-based). */
export const backoffMs = (attempt: number): number =>
  REGISTER_RETRY_BASE_MS * 2 ** Math.max(0, attempt)
